import { CLASSIFICATION, RISK_CATEGORIES, emptyRenewal } from "./emptyRenewal.js";

function TableEditor({ title, columns, rows, onChange }) {
    return (
        <fieldset className="commercial-proposal__table-edit">
            <legend>{title}</legend>
            {(rows || []).map((row, index) => (
                <div className="commercial-proposal__table-row" key={`${title}-${index}`}>
                    {columns.map((column) => (
                        <label key={column.key}>
                            {column.label}
                            <input
                                aria-label={`${title} ${column.label} ${index + 1}`}
                                value={row[column.key] || ""}
                                onChange={(event) => onChange(index, column.key, event.target.value)}
                            />
                        </label>
                    ))}
                </div>
            ))}
        </fieldset>
    );
}

const RenewalForm = ({ draft, onPatch, scrollPreviewTo, blurPreviewField }) => {
    const renewal = { ...emptyRenewal(), ...(draft.renewal || {}) };

    function setRenewal(fields, liveFields = fields) {
        onPatch({ renewal: { ...renewal, ...fields } }, liveFields);
    }

    function setStructural(fields) {
        onPatch({ renewal: { ...renewal, ...fields } });
    }

    function setCell(table, index, field, value) {
        const rows = (renewal[table] || []).map((row, rowIndex) => (
            rowIndex === index ? { ...row, [field]: value } : row
        ));
        onPatch(
            { renewal: { ...renewal, [table]: rows } },
            { [`${table}-${index}-${field}`]: value },
        );
    }

    function focus(section, field) {
        return () => scrollPreviewTo(section, field);
    }

    return (
        <>
            <div className="commercial-proposal__group">Internal only</div>
            <p className="commercial-proposal__hint">These notes shape the story. They do not appear in the package.</p>
            <label>
                Overall classification
                <select value={renewal.riskCategory || ""} onChange={(event) => setRenewal({ riskCategory: event.target.value }, { riskCategory: event.target.value })}>
                    <option value="">Choose</option>
                    {RISK_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
            </label>
            <label>
                Rating
                <select value={renewal.riskRating || ""} onChange={(event) => setRenewal({ riskRating: event.target.value }, { riskRating: event.target.value })}>
                    <option value="">Choose</option>
                    <option value="green">Green</option>
                    <option value="yellow">Yellow</option>
                    <option value="red">Red</option>
                </select>
            </label>
            <label>
                Client and broker context
                <textarea
                    rows={4}
                    value={renewal.clientContext || ""}
                    placeholder="Industry, relationship, broker stance, competitors, and the client's stated priorities."
                    onChange={(event) => setRenewal({ clientContext: event.target.value }, { clientContext: event.target.value })}
                />
            </label>

            <div className="commercial-proposal__group">Cover</div>
            <label>
                Company
                <input
                    value={draft.companyName || ""}
                    onFocus={focus("cover", "companyName")}
                    onBlur={() => blurPreviewField("companyName")}
                    onChange={(event) => onPatch({ companyName: event.target.value })}
                />
            </label>
            <label>
                Plan year
                <input
                    value={renewal.planYear || ""}
                    placeholder="2027"
                    onFocus={focus("cover", "planYear")}
                    onBlur={() => blurPreviewField("planYear")}
                    onChange={(event) => setRenewal({ planYear: event.target.value })}
                />
            </label>
            <label>
                Effective date
                <input
                    type="date"
                    value={draft.effectiveDate || ""}
                    onFocus={focus("cover", "effectiveDate")}
                    onBlur={() => blurPreviewField("effectiveDate")}
                    onChange={(event) => onPatch({ effectiveDate: event.target.value })}
                />
            </label>

            <div className="commercial-proposal__group">1. Executive summary</div>
            <label>
                Prior-carrier increase (%)
                <input
                    value={renewal.priorIncrease || ""}
                    placeholder="18"
                    onFocus={focus("executiveSummary", "priorIncrease")}
                    onBlur={() => blurPreviewField("priorIncrease")}
                    onChange={(event) => setRenewal({ priorIncrease: event.target.value })}
                />
            </label>
            <label>
                Prior carrier
                <input
                    value={renewal.priorCarrier || ""}
                    onFocus={focus("executiveSummary", "priorCarrier")}
                    onBlur={() => blurPreviewField("priorCarrier")}
                    onChange={(event) => setRenewal({ priorCarrier: event.target.value })}
                />
            </label>
            <label>
                Value delivered
                <input
                    value={renewal.glanceValue || ""}
                    placeholder="$ or %"
                    onFocus={focus("executiveSummary", "glanceValue")}
                    onBlur={() => blurPreviewField("glanceValue")}
                    onChange={(event) => setRenewal({ glanceValue: event.target.value })}
                />
            </label>
            <label>
                Partnership Plan adoption
                <input
                    value={renewal.glanceAdoption || ""}
                    onFocus={focus("executiveSummary", "glanceAdoption")}
                    onBlur={() => blurPreviewField("glanceAdoption")}
                    onChange={(event) => setRenewal({ glanceAdoption: event.target.value })}
                />
            </label>
            <label>
                Claims and utilization
                <input
                    value={renewal.glanceClaims || ""}
                    onFocus={focus("executiveSummary", "glanceClaims")}
                    onBlur={() => blurPreviewField("glanceClaims")}
                    onChange={(event) => setRenewal({ glanceClaims: event.target.value })}
                />
            </label>
            <label>
                Clinical and engagement
                <input
                    value={renewal.glanceClinical || ""}
                    onFocus={focus("executiveSummary", "glanceClinical")}
                    onBlur={() => blurPreviewField("glanceClinical")}
                    onChange={(event) => setRenewal({ glanceClinical: event.target.value })}
                />
            </label>
            <label>
                Recommended next steps
                <textarea
                    rows={3}
                    value={renewal.nextSteps || ""}
                    placeholder="What this client should decide, and by when."
                    onFocus={focus("executiveSummary", "nextSteps")}
                    onBlur={() => blurPreviewField("nextSteps")}
                    onChange={(event) => setRenewal({ nextSteps: event.target.value })}
                />
            </label>

            <div className="commercial-proposal__group">2. Year in review</div>
            <label>
                Why you came to Centivo
                <input
                    value={renewal.switchDriver || ""}
                    placeholder="The original switch driver"
                    onFocus={focus("yearInReview", "switchDriver")}
                    onBlur={() => blurPreviewField("switchDriver")}
                    onChange={(event) => setRenewal({ switchDriver: event.target.value })}
                />
            </label>
            <label>
                What happened this year
                <textarea
                    rows={5}
                    value={renewal.yearInReview || ""}
                    onFocus={focus("yearInReview", "yearInReview")}
                    onBlur={() => blurPreviewField("yearInReview")}
                    onChange={(event) => setRenewal({ yearInReview: event.target.value })}
                />
            </label>

            <div className="commercial-proposal__group">3. Financial performance</div>
            <label>
                Renewal classification
                <select
                    value={renewal.classification || "fair"}
                    onFocus={focus("financialPerformance", "classificationLabel")}
                    onBlur={() => blurPreviewField("classificationLabel")}
                    onChange={(event) => {
                        const item = CLASSIFICATION[event.target.value] || CLASSIFICATION.fair;
                        setRenewal(
                            { classification: event.target.value },
                            { classificationLabel: item.label, classificationNote: item.note },
                        );
                    }}
                >
                    {Object.entries(CLASSIFICATION).map(([value, item]) => (
                        <option key={value} value={value}>{item.label}</option>
                    ))}
                </select>
            </label>
            <TableEditor
                title="Benchmarks"
                columns={[
                    { key: "metric", label: "Metric" },
                    { key: "centivo", label: "Centivo" },
                    { key: "milliman", label: "Milliman" },
                    { key: "regional", label: "Regional" },
                ]}
                rows={renewal.benchmarks}
                onChange={(index, field, value) => setCell("benchmarks", index, field, value)}
            />
            <label>
                Medical
                <textarea rows={2} value={renewal.driverMedical || ""} placeholder="Trend, frequency, severity, large claimants" onFocus={focus("financialPerformance", "driverMedical")} onBlur={() => blurPreviewField("driverMedical")} onChange={(event) => setRenewal({ driverMedical: event.target.value })} />
            </label>
            <label>
                Pharmacy
                <textarea rows={2} value={renewal.driverPharmacy || ""} placeholder="Specialty, GLP-1, high-cost medications" onFocus={focus("financialPerformance", "driverPharmacy")} onBlur={() => blurPreviewField("driverPharmacy")} onChange={(event) => setRenewal({ driverPharmacy: event.target.value })} />
            </label>
            <label>
                Stop loss
                <textarea rows={2} value={renewal.driverStopLoss || ""} placeholder="Specific, aggregate, market conditions" onFocus={focus("financialPerformance", "driverStopLoss")} onBlur={() => blurPreviewField("driverStopLoss")} onChange={(event) => setRenewal({ driverStopLoss: event.target.value })} />
            </label>
            <label>
                Administrative
                <textarea rows={2} value={renewal.driverAdmin || ""} placeholder="Deferred admin, pricing corrections, census" onFocus={focus("financialPerformance", "driverAdmin")} onBlur={() => blurPreviewField("driverAdmin")} onChange={(event) => setRenewal({ driverAdmin: event.target.value })} />
            </label>

            <div className="commercial-proposal__group">4. What Centivo saved you</div>
            <p className="commercial-proposal__hint">Leave the dollar blank until underwriting signs off on the comparison.</p>
            <label>
                Renewal increase (%)
                <input value={renewal.roiIncrease || ""} onFocus={focus("savings", "roiIncrease")} onBlur={() => blurPreviewField("roiIncrease")} onChange={(event) => setRenewal({ roiIncrease: event.target.value })} />
            </label>
            <label>
                Estimated savings
                <input value={renewal.roiSavings || ""} placeholder="Amount, without the dollar sign" onFocus={focus("savings", "roiSavings")} onBlur={() => blurPreviewField("roiSavings")} onChange={(event) => setRenewal({ roiSavings: event.target.value })} />
            </label>
            <TableEditor
                title="Financial impact"
                columns={[
                    { key: "metric", label: "Metric" },
                    { key: "ppo", label: "PPO estimate" },
                    { key: "centivo", label: "Centivo" },
                    { key: "difference", label: "Difference" },
                ]}
                rows={renewal.impact}
                onChange={(index, field, value) => setCell("impact", index, field, value)}
            />
            <label>
                Avoided cost categories
                <textarea rows={5} value={renewal.avoided || ""} onFocus={focus("savings", "avoided")} onBlur={() => blurPreviewField("avoided")} onChange={(event) => setRenewal({ avoided: event.target.value })} />
            </label>
            <label className="commercial-proposal__check">
                <input type="checkbox" checked={Boolean(renewal.includeMultiYear)} onChange={(event) => setStructural({ includeMultiYear: event.target.checked })} />
                Multi-year savings (2+ years with Centivo)
            </label>
            {renewal.includeMultiYear ? (
                <label>
                    Multi-year value
                    <textarea rows={3} value={renewal.multiYear || ""} onFocus={focus("savings", "multiYear")} onBlur={() => blurPreviewField("multiYear")} onChange={(event) => setRenewal({ multiYear: event.target.value })} />
                </label>
            ) : null}

            <div className="commercial-proposal__group">5. Partnership Plan</div>
            <TableEditor
                title="Partnership Plan vs PPO"
                columns={[
                    { key: "metric", label: "Metric" },
                    { key: "partnership", label: "Partnership" },
                    { key: "ppo", label: "PPO" },
                ]}
                rows={renewal.partnership}
                onChange={(index, field, value) => setCell("partnership", index, field, value)}
            />

            <div className="commercial-proposal__group">6. Rates and options</div>
            <label>
                Population
                <select value={renewal.population || "non-bhcg"} onFocus={focus("ratesOptions", "ratesOptions")} onBlur={() => blurPreviewField("ratesOptions")} onChange={(event) => setStructural({ population: event.target.value })}>
                    <option value="non-bhcg">Non-BHCG custom rates</option>
                    <option value="bhcg">BHCG program schedule</option>
                </select>
            </label>
            {renewal.population === "bhcg" ? null : (
                <TableEditor
                    title="Rate comparison"
                    columns={[
                        { key: "line", label: "Line" },
                        { key: "current", label: "Current" },
                        { key: "proposed", label: "Proposed" },
                        { key: "change", label: "Change" },
                    ]}
                    rows={renewal.rates}
                    onChange={(index, field, value) => setCell("rates", index, field, value)}
                />
            )}
            <label>
                Renewal options
                <textarea rows={4} value={renewal.renewalOptions || ""} onFocus={focus("ratesOptions", "renewalOptions")} onBlur={() => blurPreviewField("renewalOptions")} onChange={(event) => setRenewal({ renewalOptions: event.target.value })} />
            </label>

            <div className="commercial-proposal__group">7. Recommendations</div>
            <p className="commercial-proposal__hint">Clear a category to take it off the page.</p>
            <label>
                Financial strategy
                <textarea rows={4} value={renewal.recFinancial || ""} onFocus={focus("recommendations", "recFinancial")} onBlur={() => blurPreviewField("recFinancial")} onChange={(event) => setRenewal({ recFinancial: event.target.value })} />
            </label>
            <label>
                Clinical strategy
                <textarea rows={3} value={renewal.recClinical || ""} onFocus={focus("recommendations", "recClinical")} onBlur={() => blurPreviewField("recClinical")} onChange={(event) => setRenewal({ recClinical: event.target.value })} />
            </label>
            <label>
                Engagement strategy
                <textarea rows={3} value={renewal.recEngagement || ""} onFocus={focus("recommendations", "recEngagement")} onBlur={() => blurPreviewField("recEngagement")} onChange={(event) => setRenewal({ recEngagement: event.target.value })} />
            </label>
            <label>
                Operational strategy
                <textarea rows={3} value={renewal.recOperational || ""} onFocus={focus("recommendations", "recOperational")} onBlur={() => blurPreviewField("recOperational")} onChange={(event) => setRenewal({ recOperational: event.target.value })} />
            </label>
            <label>
                Where this partnership is headed
                <textarea rows={3} value={renewal.headed || ""} onFocus={focus("recommendations", "headed")} onBlur={() => blurPreviewField("headed")} onChange={(event) => setRenewal({ headed: event.target.value })} />
            </label>

            <div className="commercial-proposal__group">8. Next steps</div>
            <label>
                Renewal decision date
                <input value={renewal.decisionDate || ""} placeholder="October 1, 2026" onFocus={focus("nextSteps", "decisionDate")} onBlur={() => blurPreviewField("decisionDate")} onChange={(event) => setRenewal({ decisionDate: event.target.value })} />
            </label>
            <label>
                Open enrollment preparation
                <input value={renewal.enrollmentWindow || ""} placeholder="August 15 – September 15" onFocus={focus("nextSteps", "enrollmentWindow")} onBlur={() => blurPreviewField("enrollmentWindow")} onChange={(event) => setRenewal({ enrollmentWindow: event.target.value })} />
            </label>
            <label>
                Implementation
                <input value={renewal.implementation || ""} onFocus={focus("nextSteps", "implementation")} onBlur={() => blurPreviewField("implementation")} onChange={(event) => setRenewal({ implementation: event.target.value })} />
            </label>
            <label className="commercial-proposal__check">
                <input type="checkbox" checked={Boolean(renewal.includeAppendix)} onChange={(event) => setStructural({ includeAppendix: event.target.checked })} />
                Appendix (network, admin, engagement, care management)
            </label>
        </>
    );
};

export default RenewalForm;
